import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import api from "src/rest/api";

interface Report {
  id: string;
  date: string;
  content: {
    error: string;
    errorInfo: {
      stack: string;
      componentStack: string;
    };
  };
  origin: string;
  type: string;
  user: string | null;
  cache: {
    cookies: string;
    localStorage: string;
    sessionStorage: string;
  };
}

const ReportsTable: React.FC = () => {
  const [email, setEmail] = useState("");
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchReports = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.post("/user-reports/list", { email });
      setReports(response.data);
    } catch (err) {
      setError("Failed to fetch reports");
    } finally {
      setLoading(false);
    }
  };

  const saveCurrentState = () => {
    const currentCookies = Cookies.get();
    const currentLocalStorage = { ...localStorage };
    const currentSessionStorage = { ...sessionStorage };

    return { currentCookies, currentLocalStorage, currentSessionStorage };
  };

  const restorePreviousState = (state: any) => {
    // Restore cookies
    Object.keys(state.currentCookies).forEach((key) => {
      Cookies.set(key, state.currentCookies[key]);
    });

    // Restore localStorage
    Object.keys(state.currentLocalStorage).forEach((key) => {
      localStorage.setItem(key, state.currentLocalStorage[key]);
    });

    // Restore sessionStorage
    Object.keys(state.currentSessionStorage).forEach((key) => {
      sessionStorage.setItem(key, state.currentSessionStorage[key]);
    });
  };

  const handleReproduceError = (report: Report) => {
    const previousState = saveCurrentState();

    // Set cookies
    const cookies = JSON.parse(report.cache.cookies);
    for (const [key, value] of Object.entries(cookies)) {
      Cookies.set(key, value as string);
    }

    // Set localStorage
    const localStorageData = JSON.parse(report.cache.localStorage);
    for (const [key, value] of Object.entries(localStorageData)) {
      localStorage.setItem(key, value as string);
    }

    // Set sessionStorage
    const sessionStorageData = JSON.parse(report.cache.sessionStorage);
    for (const [key, value] of Object.entries(sessionStorageData)) {
      sessionStorage.setItem(key, value as string);
    }

    // Extract the path after the domain from the origin
    const path = new URL(report.origin).pathname;

    // Reload the page at the origin of the error
    window.location.href = path;

    // Restore previous state when the component is unmounted
    return () => restorePreviousState(previousState);
  };

  useEffect(() => {
    return () => {
      // Restore previous state when the component is unmounted
      restorePreviousState(saveCurrentState());
    };
  }, []);

  return (
    <div className="mt-10">
      <h2 className="text-xl font-bold">User Reports</h2>
      <div className="mb-4">
        <input
          type="email"
          placeholder="Filter by email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mr-2 rounded border p-2"
        />
        <button
          onClick={fetchReports}
          className="rounded bg-blue-600 p-2 text-white"
        >
          Fetch Reports
        </button>
      </div>
      {loading && <p>Loading...</p>}
      {error && <p className="text-red-500">{error}</p>}
      {reports && reports.length === 0 && <p>No reports found</p>}
      <table className="min-w-full bg-white text-xs">
        <thead>
          <tr>
            <th className="py-2">ID</th>
            <th className="py-2">Date</th>
            <th className="py-2">Error</th>
            <th className="py-2">Origin</th>
            <th className="py-2">Type</th>
            <th className="py-2">User</th>
            <th className="py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {reports.map((report) => (
            <tr key={report.id}>
              <td className="border px-4 py-2">{report.id}</td>
              <td className="border px-4 py-2">
                {new Date(report.date).toLocaleString()}
              </td>
              <td className="border px-4 py-2">{report.content.error}</td>
              <td className="border px-4 py-2">{report.origin}</td>
              <td className="border px-4 py-2">{report.type}</td>
              <td className="border px-4 py-2">{report.user}</td>
              <td className="border px-4 py-2">
                <button
                  onClick={() => handleReproduceError(report)}
                  className="rounded bg-blue-600 p-2 text-white"
                >
                  Reproduce Error
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ReportsTable;
